import React, { useEffect } from "react";
import PropTypes from "prop-types";

export default function Dialog({
  title,
  body,
  negativeButton2,
  positiveButton1,
  showDialog,
  setShowDialog,
  footer,
  bodyPaddingClass,
  onModalclick,
  noCenter,
}) {
  // Escape closes — a second way out that can't be pushed off-screen by a long
  // body, whatever the caller's layout does.
  useEffect(() => {
    if (!showDialog) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowDialog(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [showDialog, setShowDialog]);

  return (
    <>
      {showDialog ? (
        <>
          <div
            onClick={onModalclick}
            className={`flex overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none ${
              !noCenter
                ? "justify-center items-center"
                : "justify-end items-start"
            }`}
          >
            <div className="relative w-auto my-6 mx-5 max-w-md max-h-[calc(100vh-3rem)] flex">
              {/*content*/}
              {/* max-h + min-h-0 on the body below: without a height bound a
                  long body grows past the viewport and takes the header and
                  footer with it, leaving no reachable close button. */}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full max-h-full bg-white outline-none focus:outline-none">
                {/*header*/}
                {title && (
                  <div className="flex flex-shrink-0 items-start justify-between p-4 border-b border-solid border-slate-200 rounded-t">
                    <h3 className="text-xl font-semibold">{title}</h3>
                    {/* Was opacity-5 — 5% opacity, so the close control was
                        invisible and the dialog looked unclosable. */}
                    <button
                      type="button"
                      aria-label="Close"
                      className="p-1 ml-auto bg-transparent border-0 float-right text-3xl leading-none font-semibold text-slate-400 hover:text-slate-700 transition-colors outline-none focus:outline-none"
                      onClick={() => setShowDialog(false)}
                    >
                      <span className="bg-transparent h-6 w-6 text-2xl block outline-none focus:outline-none">
                        ×
                      </span>
                    </button>
                  </div>
                )}
                {/*body*/}
                <div
                  className={`relative ${
                    bodyPaddingClass ? bodyPaddingClass : "p-6"
                  } flex-auto min-h-0 overflow-y-auto`}
                >
                  <p className="my-4 text-slate-500 text-lg leading-relaxed whitespace-pre-line">
                    {body}
                  </p>
                </div>
                {/*footer*/}
                {(!!negativeButton2 || !!positiveButton1) && (
                  <div className="flex flex-shrink-0 items-center justify-end p-3 border-t border-solid border-slate-200 rounded-b">
                    {negativeButton2 && negativeButton2.text && (
                      <button
                        className="bg-rose-500 text-white active:bg-rose-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                        type="button"
                        onClick={() => {
                          negativeButton2.callback();
                          setShowDialog(false);
                        }}
                      >
                        {negativeButton2.text}
                      </button>
                    )}
                    {positiveButton1 && positiveButton1.text && (
                      <button
                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-8 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                        type="button"
                        onClick={() => {
                          positiveButton1.callback();
                          setShowDialog(false);
                        }}
                      >
                        {positiveButton1.text}
                      </button>
                    )}
                  </div>
                )}

                {footer?.text && (
                  <div
                    onClick={footer?.callback}
                    class="w-full cursor-pointer text-center text-gray-500 uppercase text-sm py-2"
                  >
                    {footer?.text}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </>
  );
}

Dialog.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  positiveButton1: PropTypes.shape({
    text: PropTypes.string.isRequired,
    callback: PropTypes.func.isRequired,
  }).isRequired,
  negativeButton2: PropTypes.shape({
    text: PropTypes.string.isRequired,
    callback: PropTypes.func,
  }),
  showDialog: PropTypes.bool.isRequired,
  setShowDialog: PropTypes.func.isRequired,
};
